import { supabase, isSupabaseConfigured } from './client';
import { storage } from './storage';

/**
 * Real Database Queries for VJ Dsign Studio
 * Pure Supabase queries - Zero mock data, Zero localStorage
 */

export const queries = {
  // ==========================================
  // DASHBOARD METRICS (Real SQL Counts)
  // ==========================================
  async getDashboardMetrics() {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase not configured in environment.');
    }

    // Execute real counts in parallel
    const [
      totalProjectsRes,
      publishedProjectsRes,
      draftProjectsRes,
      servicesRes,
      packagesRes,
      galleryRes,
      testimonialsRes,
      newEnquiriesRes,
      inProgressEnquiriesRes,
      recentEnquiriesRes,
      recentProjectsRes,
    ] = await Promise.all([
      supabase.from('projects').select('*', { count: 'exact', head: true }),
      supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'published'),
      supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'draft'),
      supabase.from('services').select('*', { count: 'exact', head: true }),
      supabase.from('packages').select('*', { count: 'exact', head: true }),
      supabase.from('gallery').select('*', { count: 'exact', head: true }),
      supabase.from('testimonials').select('*', { count: 'exact', head: true }),
      supabase.from('enquiries').select('*', { count: 'exact', head: true }).eq('status', 'new'),
      supabase.from('enquiries').select('*', { count: 'exact', head: true }).eq('status', 'in_progress'),
      supabase.from('enquiries').select('*').order('created_at', { ascending: false }).limit(5),
      supabase.from('projects').select('*').order('created_at', { ascending: false }).limit(5),
    ]);

    return {
      totalProjects: totalProjectsRes.count || 0,
      publishedProjects: publishedProjectsRes.count || 0,
      draftProjects: draftProjectsRes.count || 0,
      totalServices: servicesRes.count || 0,
      totalPackages: packagesRes.count || 0,
      totalGallery: galleryRes.count || 0,
      totalTestimonials: testimonialsRes.count || 0,
      newEnquiries: newEnquiriesRes.count || 0,
      inProgressEnquiries: inProgressEnquiriesRes.count || 0,
      recentEnquiries: recentEnquiriesRes.data || [],
      recentProjects: recentProjectsRes.data || [],
    };
  },

  // ==========================================
  // PROJECTS
  // ==========================================
  projects: {
    async getAll() {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(*)')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },

    async getPublished() {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(*)')
        .eq('status', 'published')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async create(project) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const slug = project.slug || project.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const { data, error } = await supabase
        .from('projects')
        .insert([{ ...project, slug }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async toggleStatus(id, currentStatus) {
      const nextStatus = currentStatus === 'published' ? 'draft' : 'published';
      return this.update(id, { status: nextStatus });
    },

    async toggleFeatured(id, currentFeatured) {
      return this.update(id, { featured: !currentFeatured });
    },

    async delete(id) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      // Fetch associated images to delete storage objects
      const { data: images } = await supabase
        .from('project_images')
        .select('storage_path')
        .eq('project_id', id);

      if (images && images.length > 0) {
        for (const img of images) {
          if (img.storage_path) {
            try {
              await storage.deleteFile('project-images', img.storage_path);
            } catch (err) {
              console.warn('Could not remove storage object:', img.storage_path);
            }
          }
        }
      }

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },

    async addImage(projectId, imageUrl, storagePath = '', altText = '', sortOrder = 0) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('project_images')
        .insert([{
          project_id: projectId,
          image_url: imageUrl,
          storage_path: storagePath,
          alt_text: altText,
          sort_order: sortOrder,
        }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async deleteImage(imageId, storagePath = '') {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (storagePath) {
        try {
          await storage.deleteFile('project-images', storagePath);
        } catch (err) {
          console.warn('Could not remove storage image:', storagePath);
        }
      }
      const { error } = await supabase
        .from('project_images')
        .delete()
        .eq('id', imageId);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // SERVICES
  // ==========================================
  services: {
    async getAll() {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async getActive() {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('status', 'active')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async create(service) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const slug = service.slug || service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const { data, error } = await supabase
        .from('services')
        .insert([{ ...service, slug }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async toggleStatus(id, currentStatus) {
      const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
      return this.update(id, { status: nextStatus });
    },

    async delete(id) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // PACKAGES
  // ==========================================
  packages: {
    async getAll() {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async getActive() {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('status', 'active')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async create(pkg) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const slug = pkg.slug || pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const { data, error } = await supabase
        .from('packages')
        .insert([{
          ...pkg,
          slug,
          features: Array.isArray(pkg.features) ? pkg.features : [],
        }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('packages')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async toggleStatus(id, currentStatus) {
      const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
      return this.update(id, { status: nextStatus });
    },

    async togglePopular(id, currentPopular) {
      return this.update(id, { popular: !currentPopular });
    },

    async delete(id) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { error } = await supabase
        .from('packages')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // GALLERY
  // ==========================================
  gallery: {
    async getAll() {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },

    async getPublished() {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .eq('status', 'published')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async create(item) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('gallery')
        .insert([item])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('gallery')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async toggleStatus(id, currentStatus) {
      const nextStatus = currentStatus === 'published' ? 'draft' : 'published';
      return this.update(id, { status: nextStatus });
    },

    async toggleFeatured(id, currentFeatured) {
      return this.update(id, { featured: !currentFeatured });
    },

    async delete(id, storagePath = '') {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (storagePath) {
        try {
          await storage.deleteFile('gallery', storagePath);
        } catch (err) {
          console.warn('Could not delete gallery storage item:', storagePath);
        }
      }
      const { error } = await supabase
        .from('gallery')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // TESTIMONIALS
  // ==========================================
  testimonials: {
    async getAll() {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },

    async getPublished() {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('status', 'published')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },

    async create(item) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('testimonials')
        .insert([item])
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async update(id, updates) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('testimonials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async toggleStatus(id, currentStatus) {
      const nextStatus = currentStatus === 'published' ? 'draft' : 'published';
      return this.update(id, { status: nextStatus });
    },

    async delete(id, storagePath = '') {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (storagePath) {
        try {
          await storage.deleteFile('testimonial-images', storagePath);
        } catch (err) {
          console.warn('Could not remove testimonial avatar:', storagePath);
        }
      }
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // ENQUIRIES (Public INSERT, Admin Only SELECT)
  // ==========================================
  enquiries: {
    async submit(enquiry) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { error } = await supabase
        .from('enquiries')
        .insert([{
          name: enquiry.name.trim(),
          email: enquiry.email.trim(),
          phone: enquiry.phone ? enquiry.phone.trim() : null,
          subject: enquiry.subject ? enquiry.subject.trim() : 'General Inquiry',
          message: enquiry.message.trim(),
          status: 'new',
        }]);
      if (error) throw error;
      return { success: true };
    },

    async getAll(statusFilter = 'all') {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      let query = supabase
        .from('enquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },

    async updateStatus(id, status, adminNotes = null) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const updates = { status };
      if (adminNotes !== null) {
        updates.admin_notes = adminNotes;
      }
      const { data, error } = await supabase
        .from('enquiries')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async delete(id) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { error } = await supabase
        .from('enquiries')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
  },

  // ==========================================
  // SITE SETTINGS
  // ==========================================
  siteSettings: {
    async getAll() {
      if (!isSupabaseConfigured) return {};
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');
      if (error) throw error;
      const settingsMap = {};
      (data || []).forEach((row) => {
        settingsMap[row.setting_key] = row.setting_value;
      });
      return settingsMap;
    },

    async get(key) {
      if (!isSupabaseConfigured) return null;
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', key)
        .maybeSingle();
      if (error) throw error;
      return data ? data.setting_value : null;
    },

    async upsert(key, value) {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('site_settings')
        .upsert(
            { setting_key: key, setting_value: value, updated_at: new Date().toISOString() },
            { onConflict: 'setting_key' }
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
};

export default queries;
